import { useMemo, type CSSProperties, type RefObject } from "react";
import QRCode from "react-qr-code";
import type { ScreenAnchor } from "@shared/types/foyer";
import { useScheme, type ResolvedCustomItem } from "../schemeContext";
import { useFocusGrid } from "../useFocusGrid";

/**
 * The Custom screen: the one page a premise DESIGNS rather than fills in.
 *
 * Deliberately shared by every template and styled by none of them. Everywhere
 * else the template owns placement and look and the operator owns the data
 * (docs/template-system.md); here that is inverted, because the whole point of
 * the screen is that it carries a property's own spa page / event page /
 * whatever the template never anticipated. The only thing a template supplies
 * is the chrome of the bottom bar, so the page still feels like it belongs to
 * the launcher it was opened from.
 *
 * Structure: a full-bleed picture, a vignette over it, words and a QR anchored
 * to a nine-point grid, and a bar of 1-4 items along the bottom. Moving between
 * items changes ALL of it - each item is its own composition, which is what
 * makes one screen enough for a small slideshow the resident drives.
 */

export interface CustomScreenStyle {
  /** Bar background behind the items. */
  barBg: string;
  /** Item label colour. */
  ink: string;
  /** Focused item's fill + the focus ring. */
  accent: string;
  /** Text colour ON the accent fill. */
  onAccent: string;
  /** Body font stack. */
  font: string;
}

interface Props {
  style: CustomScreenStyle;
  /** Leaves the screen (the Back key, and the bar's own "up and out"). */
  onBack: () => void;
  scopeRef: RefObject<HTMLElement>;
}

/**
 * The nine anchors as flex alignment. Padding keeps every anchor inside the
 * TV-safe area, and the bottom row clears the menu bar - the two ways a
 * free-position editor would have let an operator lose their own text.
 */
const SAFE_X = 96;
const SAFE_Y = 72;
/** Height the bottom bar reserves, so bottom-anchored content sits above it. */
const BAR_SPACE = 220;

export function anchorBox(anchor: ScreenAnchor): CSSProperties {
  const [row, col] = anchor.split("-") as [
    "top" | "middle" | "bottom",
    "left" | "center" | "right",
  ];
  return {
    position: "absolute",
    inset: 0,
    display: "flex",
    padding: `${SAFE_Y}px ${SAFE_X}px ${BAR_SPACE}px`,
    alignItems:
      row === "top" ? "flex-start" : row === "bottom" ? "flex-end" : "center",
    justifyContent:
      col === "left" ? "flex-start" : col === "right" ? "flex-end" : "center",
    textAlign: col === "right" ? "right" : col === "center" ? "center" : "left",
    pointerEvents: "none",
  };
}

/**
 * The vignette: a radial hole in a wash of the chosen colour. `size` is how far
 * the clear centre reaches (100 = no darkening at all, 0 = the colour edge to
 * edge), so the operator's slider reads as "how much picture do I keep".
 */
function vignetteStyle(size: number, color: string): CSSProperties {
  const clear = Math.max(0, Math.min(100, size));
  return {
    position: "absolute",
    inset: 0,
    background: `radial-gradient(ellipse at center, transparent ${clear * 0.6}%, ${color} 100%)`,
    pointerEvents: "none",
  };
}

/** Items worth rendering - one with nothing on it would paint a black page. */
export function playableItems(
  items: ResolvedCustomItem[]
): ResolvedCustomItem[] {
  return items.filter(
    (it) => it.imageUrl || it.text?.body.trim() || it.qr?.url.trim()
  );
}

export default function CustomScreen({ style, onBack, scopeRef }: Props) {
  const scheme = useScheme();
  const items = useMemo(
    () => playableItems(scheme.content.custom?.items ?? []),
    [scheme.content.custom]
  );
  // One row of items. Enter does nothing on purpose: the items exist to change
  // the page, and a press that promised more than that would be a lie. Back is
  // the only way out, which is why it is wired to the grid as well as to the
  // template's own Back handling.
  const [focus] = useFocusGrid({
    rows: 1,
    cols: Math.max(1, items.length),
    scopeRef,
    wrap: true,
    onBack,
  });
  const active = items[focus.c] ?? items[0];

  if (items.length === 0) return null;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        fontFamily: style.font,
      }}
      data-testid="custom-screen"
    >
      {/* Every picture is mounted and cross-faded, so moving along the bar is
          instant instead of showing a decode flash on a slow panel. */}
      {items.map((it, i) => (
        <div
          key={it.id}
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: it.imageUrl ? `url(${it.imageUrl})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundColor: "#000",
            opacity: i === focus.c ? 1 : 0,
            transition: "opacity 420ms ease",
          }}
        />
      ))}

      {active?.vignette && (
        <div
          data-testid="custom-vignette"
          style={vignetteStyle(active.vignette.size, active.vignette.color)}
        />
      )}

      {active?.text?.body.trim() && (
        <div style={anchorBox(active.text.placement)}>
          <div
            data-testid="custom-text"
            style={{
              color: active.text.color,
              fontSize: active.text.fontSize,
              lineHeight: 1.25,
              fontWeight: 600,
              maxWidth: "60%",
              // The one thing the operator does not own: a shadow that keeps
              // their colour readable wherever the picture goes light.
              textShadow: "0 2px 24px rgba(0,0,0,0.45)",
              whiteSpace: "pre-wrap",
            }}
          >
            {active.text.body}
          </div>
        </div>
      )}

      {active?.qr?.url.trim() && (
        <div style={anchorBox(active.qr.placement)}>
          <div
            data-testid="custom-qr"
            style={{
              background: "#fff",
              padding: 20,
              borderRadius: 12,
              lineHeight: 0,
              boxShadow: "0 24px 60px -20px rgba(0,0,0,0.6)",
            }}
          >
            <QRCode value={active.qr.url} size={220} level="M" />
          </div>
        </div>
      )}

      <nav
        data-testid="custom-bar"
        style={{
          position: "absolute",
          left: SAFE_X,
          right: SAFE_X,
          bottom: SAFE_Y,
          display: "flex",
          gap: 20,
          justifyContent: "center",
        }}
      >
        {items.map((it, i) => {
          const on = i === focus.c;
          return (
            <div
              key={it.id}
              data-testid={`custom-item-${it.id}`}
              aria-current={on ? "true" : undefined}
              style={{
                flex: `1 1 0`,
                maxWidth: 420,
                padding: "26px 32px",
                borderRadius: 18,
                background: on ? style.accent : style.barBg,
                color: on ? style.onAccent : style.ink,
                fontSize: 30,
                fontWeight: 600,
                textAlign: "center",
                letterSpacing: "-0.01em",
                // A ring rather than a size change: four items that grow and
                // shrink make the whole bar jump on every arrow press.
                boxShadow: on ? `0 0 0 6px ${style.accent}55` : "none",
                transition: "background 160ms ease, box-shadow 160ms ease",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {it.label}
            </div>
          );
        })}
      </nav>
    </div>
  );
}
